import React, { useState, useEffect, useCallback } from 'react';
import { appCfgApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { Settings, Save, RotateCcw } from 'lucide-react';

/**
 * DisplaySettingsPage - Manage UI display settings (date formats, column widths, etc.)
 */
const DisplaySettingsPage = () => {
  const { notify } = useNotifications();

  // Data
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit state - tracks changes by setting ID
  const [editedValues, setEditedValues] = useState({});

  // Load settings
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await appCfgApi.getDisplaySettings();
      setSettings(res.data);
      setEditedValues({});
    } catch (err) {
      notify.error('Failed to load display settings');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Track edits
  const handleChange = (id, value) => {
    setEditedValues(prev => ({
      ...prev,
      [id]: value
    }));
  };

  // Get current value (edited or original)
  const getValue = (setting) => {
    if (editedValues[setting.id] !== undefined) {
      return editedValues[setting.id];
    }
    return setting.setting_Value || '';
  };

  // Check if a setting has been edited
  const isEdited = (id) => editedValues[id] !== undefined;

  const hasEdits = Object.keys(editedValues).length > 0;

  // Save all edits
  const handleSave = async () => {
    setSaving(true);
    try {
      for (const [id, value] of Object.entries(editedValues)) {
        const original = settings.find(s => s.id === parseInt(id));
        if (!original) continue;
        await appCfgApi.updateDisplaySetting(parseInt(id), {
          ...original,
          setting_Value: value
        });
      }
      notify.success(`Saved ${Object.keys(editedValues).length} setting(s)`);

      // Update local state
      setSettings(prev => prev.map(setting => {
        const newValue = editedValues[setting.id];
        if (newValue !== undefined) {
          return { ...setting, setting_Value: newValue };
        }
        return setting;
      }));
      setEditedValues({});
    } catch (err) {
      notify.error('Failed to save: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Reset edits
  const handleReset = () => {
    setEditedValues({});
  };

  if (loading) {
    return (
      <div className="dev-module display-settings-page">
        <div className="dev-module-header">
          <div className="dev-module-header-left"><h1>Display Settings</h1></div>
        </div>
        <div className="ds-panel"><p style={{ padding: 'var(--spacing-md)' }}>Loading...</p></div>
      </div>
    );
  }

  return (
    <div className="dev-module display-settings-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Settings size={20} className="stui-imgupload-title-icon" />
            Display Settings
          </h1>
          <span className="dev-module-meta">
            {settings.length} settings
          </span>
        </div>
        <div className="dev-module-header-right">
          <button
            className="crud-btn"
            onClick={handleReset}
            disabled={!hasEdits}
            title="Discard changes"
          >
            <RotateCcw size={18} />
          </button>
          <button
            className="crud-btn primary"
            onClick={handleSave}
            disabled={saving || !hasEdits}
            title="Save Changes"
          >
            <Save size={18} />
          </button>
        </div>
      </div>

      {/* Panel mirrors lookup-values-panel */}
      <div className="ds-panel">
        <div className="ds-panel-header">
          <h3>UI Display Formats & Defaults</h3>
          <span className="ds-panel-count">{settings.length} settings</span>
        </div>
        <div className="ds-scroll">
          <table className="ds-table">
            <thead>
              <tr>
                <th style={{ width: 200 }}>Setting</th>
                <th style={{ width: 250 }}>Value</th>
                <th style={{ width: 100 }}>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {settings.map(setting => (
                <tr key={setting.id} className={isEdited(setting.id) ? 'edited' : ''}>
                  <td className="ds-key">{setting.setting_Key}</td>
                  <td>
                    <input
                      type={setting.setting_Type === 'number' ? 'number' : 'text'}
                      value={getValue(setting)}
                      onChange={(e) => handleChange(setting.id, e.target.value)}
                      className="ds-input"
                    />
                  </td>
                  <td className="ds-type">{setting.setting_Type}</td>
                  <td className="ds-desc">{setting.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DisplaySettingsPage;
